import { CheckOutlined, CloseOutlined } from "@mui-symbols-material/w300"
import type { ReactElement } from "react"

import { DATA_TYPE_TO_LABEL_MAP } from "./constants"
import type { AssociatedTestTemplateFormValues } from "./schemas"
import type { AssociatedTestTemplateFormPayload } from "./types"

import type { AssociatedTestTemplate } from "@/graphql/codegen/graphql"
import { DataType, TestTemplateComparison } from "@/graphql/codegen/graphql"
import { toNumberOrNull, toStringOrEmpty, toStringOrNull } from "@/utils/forms"

export const renderCheckMarkIcon = (value: boolean): ReactElement => {
  return value ? <CheckOutlined className='text-green-700' /> : <CloseOutlined className='text-gray-400' />
}

export const getDataTypeLabel = (dataType?: DataType | null): string => {
  if (!dataType) return ""

  return DATA_TYPE_TO_LABEL_MAP[dataType] ?? dataType
}

export const hasSignificantFigures = (comparison: TestTemplateComparison, dataType?: DataType | null): boolean => {
  return (
    [TestTemplateComparison.Minimum, TestTemplateComparison.Maximum].includes(comparison) ||
    (comparison === TestTemplateComparison.Range && dataType === DataType.Numeric)
  )
}

export const formatAssociatedTestTemplateLimits = (associatedTestTemplate: AssociatedTestTemplate): string => {
  const { equalToValue, minimumValue, maximumValue, testTemplate } = associatedTestTemplate
  const comparison = testTemplate?.comparison

  switch (comparison) {
    case TestTemplateComparison.EqualTo:
      return equalToValue!
    case TestTemplateComparison.Minimum:
      return minimumValue!
    case TestTemplateComparison.Maximum:
      return maximumValue!
    case TestTemplateComparison.Range:
      return `${minimumValue} - ${maximumValue}`
    default:
      return ""
  }
}

export const getDefaultAssociatedTestTemplateValues = (
  associatedTestTemplate: AssociatedTestTemplate | null
): AssociatedTestTemplateFormValues => ({
  equalToValue: toStringOrEmpty(associatedTestTemplate?.equalToValue),
  maximumValue: toStringOrEmpty(associatedTestTemplate?.maximumValue),
  minimumValue: toStringOrEmpty(associatedTestTemplate?.minimumValue),
  dataType: associatedTestTemplate?.dataType ?? "",
  significantFigures: toStringOrEmpty(associatedTestTemplate?.significantFigures),
  printToAkrochem: associatedTestTemplate?.printToAkrochem ?? null,
})

// Only include fields that are visible based on the comparison type.
export const serializeAssociatedTestTemplateForm = (
  formValues: AssociatedTestTemplateFormValues,
  comparison: TestTemplateComparison
): AssociatedTestTemplateFormPayload => {
  const payload: AssociatedTestTemplateFormPayload = {
    printToAkrochem: formValues.printToAkrochem ?? false,
  }

  const dataType = toStringOrNull(formValues.dataType) as DataType | null

  switch (comparison) {
    case TestTemplateComparison.EqualTo:
      payload.equalToValue = toStringOrNull(formValues.equalToValue)
      break
    case TestTemplateComparison.Minimum:
      payload.minimumValue = toStringOrNull(formValues.minimumValue)
      break
    case TestTemplateComparison.Maximum:
      payload.maximumValue = toStringOrNull(formValues.maximumValue)
      break
    case TestTemplateComparison.Range:
      payload.dataType = dataType
      payload.minimumValue = toStringOrNull(formValues.minimumValue)
      payload.maximumValue = toStringOrNull(formValues.maximumValue)
      break
  }

  if (hasSignificantFigures(comparison, dataType)) {
    payload.significantFigures = toNumberOrNull(formValues.significantFigures)
  }

  return payload
}
